import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, CreditCard, Banknote, Sparkles } from "lucide-react";
import { ReferralWallet, RedemptionType } from "@/contexts/ReferralContext";

const redemptionOptions: { type: RedemptionType; label: string; icon: typeof CreditCard; description: string }[] = [
  { type: "subscription_discount", label: "Subscription Discount", icon: CreditCard, description: "Apply to next billing cycle" },
  { type: "cash_payout", label: "Cash Payout", icon: Banknote, description: "Withdraw to bank (min ₹500)" },
  { type: "platform_credits", label: "Platform Credits", icon: Sparkles, description: "Use across AccouPro services" },
];

interface Props {
  wallet: ReferralWallet;
  onRedeem: (amount: number, type: RedemptionType) => boolean;
}

export default function WalletCard({ wallet, onRedeem }: Props) {
  const [redeemAmount, setRedeemAmount] = useState("");
  const [selectedType, setSelectedType] = useState<RedemptionType | null>(null);

  const handleRedeem = () => {
    const amt = Number(redeemAmount);
    if (!selectedType) { toast.error("Select a redemption method"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (selectedType === "cash_payout" && amt < 500) { toast.error("Minimum ₹500 for cash payout"); return; }
    if (amt > wallet.balance) { toast.error("Insufficient wallet balance"); return; }

    const ok = onRedeem(amt, selectedType);
    if (ok) {
      toast.success(`₹${amt} redeemed as ${redemptionOptions.find(o => o.type === selectedType)?.label}`);
      setRedeemAmount("");
      setSelectedType(null);
    }
  };

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card space-y-4">
      {/* Balance */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
            <Wallet className="h-6 w-6 text-accent" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Referral Wallet</p>
            <p className="text-3xl font-bold font-display text-foreground">₹{wallet.balance}</p>
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="flex items-center gap-1 text-xs text-success">
            <ArrowDownCircle className="h-3 w-3" /> Earned: ₹{wallet.totalEarned}
          </div>
          <div className="flex items-center gap-1 text-xs text-destructive">
            <ArrowUpCircle className="h-3 w-3" /> Redeemed: ₹{wallet.totalRedeemed}
          </div>
        </div>
      </div>

      {/* Redemption Options */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2">Redeem As</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {redemptionOptions.map((opt) => (
            <button
              key={opt.type}
              onClick={() => setSelectedType(opt.type)}
              className={`p-3 rounded-lg border text-left transition-all ${
                selectedType === opt.type
                  ? "border-accent bg-accent/10 ring-1 ring-accent/30"
                  : "border-border bg-muted/20 hover:border-accent/40"
              }`}
            >
              <opt.icon className={`h-4 w-4 mb-1.5 ${selectedType === opt.type ? "text-accent" : "text-muted-foreground"}`} />
              <p className="text-sm font-medium text-foreground">{opt.label}</p>
              <p className="text-[10px] text-muted-foreground">{opt.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Redeem Input */}
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={redeemAmount}
          onChange={(e) => setRedeemAmount(e.target.value)}
          placeholder="Enter amount to redeem"
          className="flex-1"
          min={1}
          max={wallet.balance}
        />
        <Button onClick={handleRedeem} disabled={!selectedType || !redeemAmount} className="gap-2">
          Redeem ₹{redeemAmount || "0"}
        </Button>
      </div>

      {/* Transaction History */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2">Transaction History</p>
        <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
          {wallet.transactions.map((txn) => (
            <motion.div
              key={txn.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between p-2.5 rounded-lg bg-muted/20 border border-border/50"
            >
              <div className="flex items-center gap-2 min-w-0">
                {txn.type === "credit" ? (
                  <ArrowDownCircle className="h-4 w-4 text-success flex-shrink-0" />
                ) : (
                  <ArrowUpCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{txn.description}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(txn.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
              </div>
              <Badge
                variant="outline"
                className={`text-xs font-mono flex-shrink-0 ${
                  txn.type === "credit" ? "text-success border-success/20" : "text-destructive border-destructive/20"
                }`}
              >
                {txn.type === "credit" ? "+" : "-"}₹{txn.amount}
              </Badge>
            </motion.div>
          ))}
          {wallet.transactions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No transactions yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
